import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";

/**
 * Small confirm dialog for unbind. Explicitly names what will be
 * detached, so a mis-click on a busy Bindings tab doesn't silently
 * drop a production binding.
 *
 * Restart toggle defaults ON — an unbind that doesn't roll the
 * revision leaves the pod still projecting the old C2A_SYSTEM_ENV
 * until the next natural revision, which is confusing.
 */
export default function UnbindConfirmDialog({
  open,
  target,
  submitting,
  onCancel,
  onConfirm,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [restart, setRestart] = useState(true);

  // Reset the restart toggle when the dialog opens (so a prior
  // "restart=false" doesn't leak into the next unbind).
  React.useEffect(() => {
    if (open) setRestart(true);
  }, [open]);

  const label = target?.displayName || target?.k8sName || "this binding";

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Unbind service?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Detach{" "}
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              color: isDarkMode ? "#f1f5f9" : "#0f172a",
            }}
          >
            {label}
          </Box>{" "}
          from this app.
        </DialogContentText>
        {target?.k8sName && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1,
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontFamily: "monospace",
              fontSize: "0.72rem",
            }}
          >
            source: {target.k8sName}
          </Typography>
        )}
        <Alert severity="info" variant="outlined" sx={{ mt: 2, fontSize: "0.8rem" }}>
          The Daari service itself is <b>not</b> deleted. Only this app's binding
          to it is removed. You can rebind at any time from the Bind Service button.
        </Alert>
        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={
            <Switch
              size="small"
              checked={restart}
              onChange={(e) => setRestart(e.target.checked)}
              disabled={submitting}
            />
          }
          label={
            <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
              Restart the app immediately so the removal takes effect
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(restart)}
          color="error"
          variant="contained"
          disabled={submitting}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {submitting ? "Unbinding…" : "Unbind"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
